<script setup>
import DefaultPanel from '@/components/DefaultPanel.vue';
import PreviewPanel from '@/components/SingleFileViewComponent.vue';
import { useLayout } from '@/layout/composables/layout';
import { useSidebarStore } from '@/store/sidebar-store';
import { useTipStore } from '@/store/tip-store';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import AppFooter from './AppFooter.vue';
import AppMenuTab from './AppMenuTab.vue';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
let baseUrl = import.meta.env.VITE_APP_API_URL;
const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';

const { layoutConfig, layoutState, resetMenu } = useLayout();
const tipStore = useTipStore();
const sidebarStore = useSidebarStore();
const outsideClickListener = ref(null);
const previewFile = ref({
    url: '',
    type: ''
});
const windowWidth = ref(window.innerWidth); // windowWidth 추가

const handleResize = () => {
    // handleResize 함수 추가
    windowWidth.value = window.innerWidth;
};

// isMobile computed 속성 추가
const isMobile = computed(() => {
    return windowWidth.value < 768;
});

// sidebarStyle computed 속성 추가 (z-index 제어)
const sidebarStyle = computed(() => {
    return {
        zIndex: isMobile.value && sidebarStore.isSidebarActive ? 1001 : 1000 // 모바일 & 활성화 시 z-index 증가
    };
});

watch(
    () => sidebarStore.isSidebarActive,
    (newVal) => {
        if (newVal) {
            bindOutsideClickListener();
        } else {
            unbindOutsideClickListener();
        }
        // 모바일 환경에서 사이드바 상태 변경 시,  z-index 업데이트를 위해 handleResize()호출
        if (isMobile.value) {
            handleResize();
        }
    }
);

// watch 추가 (windowWidth 변경 감지)
watch(windowWidth, (newWidth) => {
    if (newWidth < 768) {
        sidebarStore.setSidebarState(false); // 접힌 상태
    } else {
        sidebarStore.setSidebarState(true); // 펼쳐진 상태
    }
});

const containerClass = computed(() => {
    return {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive
    };
});

const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                sidebarStore.setSidebarState(false);
                resetMenu();
            }
        };
        document.addEventListener('click', outsideClickListener.value); // 수정: document에 이벤트 리스너 추가
    }
};

const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value); // 수정: document에서 이벤트 리스너 제거
        outsideClickListener.value = null;
    }
};

const isOutsideClicked = (event) => {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button'); // topbarEl 선택자 수정

    if (!sidebarEl || !topbarEl) {
        return false;
    }

    return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
};

const sidebarWidth = computed(() => {
    return sidebarStore.isSidebarActive ? '22rem' : '0';
});

const mainContainerStyle = computed(() => ({
    marginLeft: sidebarStore.isSidebarActive && !isMobile.value ? '22rem' : '0', // isMobile 추가
    transition: 'margin-left 0.3s ease'
}));

const closePreview = () => {
    previewFile.value = { url: '', type: '' };
};

const onKeydown = async (event) => {
    if (!isDevelopment) return;
    let url = '';
    let windowFeatures = '';
    switch (event.key) {
        case 'F1':
            url = `${baseUrl}/docs/view`;
            windowFeatures = 'width=1200,height=900,resizable=yes,scrollbars=yes,status=yes';
            // if (tipStore.showPopup) {
            //     tipStore.closePopup();
            // } else {
            //     tipStore.openPopup('팝업', '내용');
            // }
            break;
        case 'F2':
            url = 'https://docs.google.com/spreadsheets/d/1YDgWE4inc9Et157NcvaXjW3MknESnh4ovpjuwXgjvPA/edit?gid=1728349873#gid=1728349873';
            windowFeatures = 'width=1300,height=900,resizable=yes,scrollbars=yes,status=yes';
            break;
        case 'F3':
            url = 'http://dev.itnj.co.kr:28917/';
            windowFeatures = 'width=700,height=950,resizable=yes,scrollbars=yes,status=yes';
            break;
        case 'F4':
            url = 'http://imsform.com';
            break;
        // case 'F6':
        //     try {
        //         const filename = 'd609-8486-141a-27e9-9937';
        //         const response = await fetch(`${baseUrl}/view/${filename}`);
        //         if (response.ok) {
        //             previewFile.value = {
        //                 url: `${baseUrl}/view/${filename}`,
        //                 type: response.headers.get('content-type'),
        //                 downurl: `${baseUrl}/download/${filename}`
        //             };
        //         }
        //     } catch (error) {
        //         console.error('Error previewing file:', error);
        //         toastComponentRef.value?.showToast('error', '알림', '파일 미리보기 중 오류가 발생했습니다.');
        //     }
        //     break;
        default:
            return;
    }
    if (url) {
        event.preventDefault();
        if (event.key === 'F3') {
            window.open(url, '', windowFeatures);
        } else if (event.key === 'F4') {
            window.open(url, '_blank');
        } else {
            window.open(url, '', windowFeatures);
        }
    }
};

onMounted(() => {
    if (isDevelopment) {
        window.addEventListener('keydown', onKeydown);
    }
    window.addEventListener('resize', handleResize); // resize 이벤트 리스너 추가
    handleResize(); // 초기 로드 시에도 화면 크기 확인

    nextTick(() => {
        bindOutsideClickListener();
    });
});

onUnmounted(() => {
    if (isDevelopment) {
        window.removeEventListener('keydown', onKeydown);
    }
    window.removeEventListener('resize', handleResize); // resize 이벤트 리스너 제거
    unbindOutsideClickListener(); // 컴포넌트 제거 시 클릭 리스너 제거
});
</script>

<template>
    <div class="layout-wrapper">
        <PreviewPanel v-if="previewFile.url" :fileUrl="previewFile.url" :fileType="previewFile.type" :downurl="previewFile.downurl" :width="'65rem'" :height="'60rem'" @close="closePreview" />
        <app-topbar class="layout-topbar">
            <template #tabs>
                <app-menu-tab></app-menu-tab>
            </template>
        </app-topbar>
        <div class="layout-main-container">
            <app-sidebar class="layout-sidebar" :class="{ active: sidebarStore.isSidebarActive }" :style="sidebarStyle"></app-sidebar>
            <div v-if="isMobile && sidebarStore.isSidebarActive" class="layout-mask-mobile" @click="sidebarStore.setSidebarState(false)"></div>
            <div class="layout-content" :style="mainContainerStyle">
                <router-view v-slot="{ Component }">
                    <keep-alive>
                        <component :is="Component" />
                    </keep-alive>
                </router-view>
                <!-- 리로드 -->
                <!-- <div class="layout-main">
                    <router-view></router-view>
                </div> -->
            </div>
        </div>
        <app-footer class="layout-footer"></app-footer>
        <div class="layout-mask"></div>
    </div>
    <div v-if="isDevelopment || (baseUrl && baseUrl.includes('dev.itnj.co.kr'))" class="system-status">
        ➜ DataBase: {{ baseUrl }}
        <br />
        ➜ Server: http://localhost:3003/
        <br />
        ➜ Mode: development
    </div>
    <Toast />
    <DefaultPanel v-if="tipStore.showPopup" :title="tipStore.popupTitle" :width="'30rem'" :height="'45rem'" @close="tipStore.closePopup">
        <template #default>
            <div v-html="tipStore.popupContent"></div>
        </template>
    </DefaultPanel>
</template>

<style scoped>
.layout-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}
.layout-topbar {
    height: 3.3rem; /* Height of the topbar */
    z-index: 1001; /* Add z-index for topbar */
}
.layout-main-container {
    display: flex;
    flex: 1;
    margin-top: 3.1rem;
    overflow: hidden;
}

.layout-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    box-sizing: border-box; /* Include padding in the element's total width and height */
}
/* 모바일 마스크 레이어 스타일 */
.layout-mask-mobile {
    position: fixed;
    top: 4.5rem; /* Topbar 아래부터 시작 */
    left: 0;
    width: 100vw;
    height: calc(100vh - 4.5rem);
    background-color: rgba(0, 0, 0, 0.4); /* 반투명 검은색 */
    z-index: 1000; /* 사이드바 바로 아래 */
}

.system-status {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 0.5rem;
    border-radius: 0.25rem;
    font-family: 'SCoreDream400';
    font-size: 0.75rem;
    z-index: 1000;
    opacity: 0.5;
}

@media (max-width: 768px) {
    .layout-topbar {
        z-index: 1002;
    }
}
</style>
